import { ApolloError } from "@apollo/client";
import { Alert, Button, Dialog, DialogContent, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { GqlPermissions } from "~/__generated__";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import FunctionTypeObservationTypeDataGrid from "../FunctionTypeObservationTypeDataGrid";
import { FunctionTypeObservationTypeDataGridFragment } from "../FunctionTypeObservationTypeDataGrid/fragments.generated";
import FunctionTypeObservationTypeForm, {
    FunctionTypeObservationTypeFormInput,
} from "../FunctionTypeObservationTypeForm";

type Props = {
    functionTypeObservationTypes: FunctionTypeObservationTypeDataGridFragment[];
    researchTypeId: string;
    functionTypeId: string;
    onAddObservationType: (data: FunctionTypeObservationTypeFormInput) => Promise<void>;
    isAddObservationTypeLoading?: boolean;
    addObservationTypeError?: ApolloError;
    onRemoveObservationType: (observationTypeId: string) => Promise<void>;
    isRemoveObservationTypeLoading: boolean;
    removeObservationTypeError?: ApolloError;
};

export default function FunctionTypeObservationTypeListCard({
    functionTypeObservationTypes,
    researchTypeId,
    functionTypeId,
    onAddObservationType,
    isAddObservationTypeLoading,
    addObservationTypeError,
    onRemoveObservationType,
    isRemoveObservationTypeLoading,
    removeObservationTypeError,
}: Props) {
    const { t } = useTranslation("functionType");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);
    const [showObservationTypeDialog, setShowObservationTypeDialog] = useState<boolean>(false);

    const handleAddObservationType = async (data: FunctionTypeObservationTypeFormInput) => {
        await onAddObservationType(data);
        setShowObservationTypeDialog(false);
    };

    return (
        <>
            {isAdmin && (
                <>
                    <Button onClick={() => setShowObservationTypeDialog(true)}>
                        {t("functionTypeObservationTypeListCard.actionAddObservationType")}
                    </Button>
                    <Dialog open={showObservationTypeDialog} onClose={() => setShowObservationTypeDialog(false)}>
                        <DialogContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {t("functionTypeObservationTypeListCard.actionAddObservationType")}
                            </Typography>
                            <FunctionTypeObservationTypeForm
                                onSubmit={handleAddObservationType}
                                isLoading={isAddObservationTypeLoading}
                                error={addObservationTypeError}
                                researchTypeId={researchTypeId}
                                functionTypeId={functionTypeId}
                            />
                        </DialogContent>
                    </Dialog>
                </>
            )}
            {removeObservationTypeError && <Alert severity="error">{removeObservationTypeError.message}</Alert>}
            <FunctionTypeObservationTypeDataGrid
                functionTypeObservationTypes={functionTypeObservationTypes}
                isAdmin={isAdmin}
                onDeleteClick={observationTypeId => onRemoveObservationType(observationTypeId)}
                isLoading={isRemoveObservationTypeLoading}
            />
        </>
    );
}
